import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ProvisioningRequest, ApiError } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@/lib/utils';
import { ArrowLeft, Loader2, Send, Save, AlertTriangle } from 'lucide-react';

const INDUSTRIES = [
  'Plumbing', 'HVAC', 'Electrical', 'Legal', 'Medical', 'Real Estate',
  'Financial Services', 'Technology', 'Consulting', 'Marketing',
  'Construction', 'Automotive', 'Retail', 'Other',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<ProvisioningRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    loadRequest();
  }, [id]);

  const loadRequest = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await api.getRequest(id);
      setRequest(data);
      setFormData(data.payload as unknown as Record<string, unknown>);
    } catch (error) {
      console.error('Failed to load request:', error);
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!id) return;
    setError('');
    setIsSaving(true);
    try {
      await api.updateRequest(id, formData);
      await loadRequest();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;
    setError('');
    setIsSubmitting(true);
    try {
      await api.updateRequest(id, formData);
      await api.submitRequest(id);
      await loadRequest();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
        </div>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-600">Request not found</p>
        </div>
      </Layout>
    );
  }

  const isDraft = request.status === 'Draft';
  const canEdit = isDraft;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {request.payload.Account_Name}
              </h1>
              <StatusBadge status={request.status} />
            </div>
            <p className="text-gray-600 mt-1">
              Created {formatDate(request.created_at)}
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {request.last_error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-600">{request.last_error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>Basic information about the client account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Account Name</Label>
                {canEdit ? (
                  <Input
                    value={formData.Account_Name as string || ''}
                    onChange={(e) => handleChange('Account_Name', e.target.value)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.Account_Name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Industry</Label>
                {canEdit ? (
                  <Select
                    value={formData.Industry as string || ''}
                    onValueChange={(value) => handleChange('Industry', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-gray-900">{request.payload.Industry}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canEdit ? (
                <>
                  <input
                    type="checkbox"
                    id="Is_AI_Receptionist_Free_Trial"
                    checked={formData.Is_AI_Receptionist_Free_Trial as boolean || false}
                    onChange={(e) => handleChange('Is_AI_Receptionist_Free_Trial', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="Is_AI_Receptionist_Free_Trial">
                    AI Receptionist Free Trial
                  </Label>
                </>
              ) : (
                <p className="text-gray-900">
                  AI Receptionist Free Trial: {request.payload.Is_AI_Receptionist_Free_Trial ? 'Yes' : 'No'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>First Name</Label>
                {canEdit ? (
                  <Input
                    value={formData.First_Name as string || ''}
                    onChange={(e) => handleChange('First_Name', e.target.value)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.First_Name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                {canEdit ? (
                  <Input
                    value={formData.Last_Name as string || ''}
                    onChange={(e) => handleChange('Last_Name', e.target.value)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.Last_Name}</p>
                )}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Email</Label>
                {canEdit ? (
                  <Input
                    type="email"
                    value={formData.Email as string || ''}
                    onChange={(e) => handleChange('Email', e.target.value)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.Email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                {canEdit ? (
                  <Input
                    value={formData.Phone as string || ''}
                    onChange={(e) => handleChange('Phone', e.target.value)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.Phone || '-'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader>
            <CardTitle>Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {canEdit ? (
              <>
                <div className="space-y-2">
                  <Label>Street</Label>
                  <Input
                    value={formData.Street as string || ''}
                    onChange={(e) => handleChange('Street', e.target.value)}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.City as string || ''}
                      onChange={(e) => handleChange('City', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select
                      value={formData.State as string || ''}
                      onValueChange={(value) => handleChange('State', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Postal Code</Label>
                    <Input
                      value={formData.Postal_Code as string || ''}
                      onChange={(e) => handleChange('Postal_Code', e.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-900">
                {[
                  request.payload.Street,
                  request.payload.City,
                  request.payload.State,
                  request.payload.Postal_Code,
                  request.payload.Country,
                ]
                  .filter(Boolean)
                  .join(', ') || '-'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Allowances */}
        <Card>
          <CardHeader>
            <CardTitle>Service Allowances</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>AI Receptionist</Label>
                {canEdit ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.AI_Receptionist_Allowance as number || 0}
                    onChange={(e) => handleChange('AI_Receptionist_Allowance', parseInt(e.target.value) || 0)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.AI_Receptionist_Allowance}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Human Receptionist</Label>
                {canEdit ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.Human_Allowance as number || 0}
                    onChange={(e) => handleChange('Human_Allowance', parseInt(e.target.value) || 0)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.Human_Allowance}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Live Chat</Label>
                {canEdit ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.LiveChat_Allowance as number || 0}
                    onChange={(e) => handleChange('LiveChat_Allowance', parseInt(e.target.value) || 0)}
                  />
                ) : (
                  <p className="text-gray-900">{request.payload.LiveChat_Allowance}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Information */}
        {request.sf_account_id && (
          <Card>
            <CardHeader>
              <CardTitle>Salesforce Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Salesforce Account ID</Label>
                <p className="text-gray-900 font-mono">{request.sf_account_id}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {canEdit && (
          <div className="flex gap-4 justify-end">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving || isSubmitting}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving || isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit for Approval
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
